import type { TFunction } from 'i18next';
import { USAGE_ACTION, USAGE_ACTION_VALUES } from '../constants/sample.constants';
import { formatSampleDateTime } from './dateFnsLocale';
import { formatEntityDisplayName } from './softBreakIdentifier';
import { formatSampleQuantityWithUnit } from './sampleUnitLabels';

export type DashboardActivityRow = {
  type: 'registration' | 'usage';
  sample_name: string | null;
  /** 同名サンプルの見分け（API sample_id） */
  sample_id?: number | null;
  /** 庫内位置の短い表示（例: 超低温冷凍庫1 · #7 · 1） */
  storage_label?: string | null;
  activity_date: string;
  action: string | null;
  detail: string | null;
  quantity: number | null;
  /** サンプル単位（空なら表示既定 vials＝本） */
  unit?: string | null;
};

function isKnownUsageAction(action: string): boolean {
  return (
    (USAGE_ACTION_VALUES as readonly string[]).includes(action) ||
    action === USAGE_ACTION.CREATED
  );
}

/** ダッシュボード API の action（DB 保存値）を表示用ラベルに */
export function dashboardUsageActionLabel(
  action: string | null | undefined,
  t: TFunction<'translation', undefined>
): string {
  if (action == null || action === '') {
    return t('dashboard.activity_used', { defaultValue: 'Used' });
  }
  switch (action) {
    case USAGE_ACTION.USE:
      return t('dashboard.activity_action_use');
    case USAGE_ACTION.DISPOSE:
      return t('dashboard.activity_action_dispose');
    case USAGE_ACTION.MOVE:
      return t('dashboard.activity_action_move');
    case USAGE_ACTION.OTHER:
      return t('dashboard.activity_action_other');
    case USAGE_ACTION.CREATED:
      return t('dashboard.activity_action_created');
    default:
      return action;
  }
}

function formatUsageQuantitySuffix(
  quantity: number | null | undefined,
  unit: string | null | undefined,
  t: TFunction<'translation', undefined>
): string {
  if (quantity == null || quantity <= 0) return '';
  return ` · ${formatSampleQuantityWithUnit(quantity, unit, t)}`;
}

type ActivitySampleRefMode = 'full' | 'locationFirst';

/**
 * 名前の直後に付ける識別子（括弧なし）。例: `#2542 · 超低温冷凍庫1 · #7 · 1`
 * locationFirst: 狭幅メタ行用。場所があれば ID を省略（ID は title / aria の full 側）。
 */
export function formatActivitySampleRef(
  activity: DashboardActivityRow,
  t?: Parameters<typeof formatEntityDisplayName>[1],
  mode: ActivitySampleRefMode = 'full'
): string {
  const id =
    typeof activity.sample_id === 'number' &&
    Number.isInteger(activity.sample_id) &&
    activity.sample_id >= 1
      ? activity.sample_id
      : null;
  const rawLoc = activity.storage_label?.trim() || '';
  const loc = rawLoc ? formatEntityDisplayName(rawLoc, t) : '';
  const parts: string[] = [];
  if (mode === 'locationFirst') {
    if (loc) parts.push(loc);
    else if (id != null) parts.push(`#${id}`);
  } else {
    if (id != null) parts.push(`#${id}`);
    if (loc) parts.push(loc);
  }
  return parts.join(' · ');
}

function withSampleRefPrefix(sampleRef: string): string {
  return sampleRef ? ` · ${sampleRef}` : '';
}

/** 最近のアクティビティ1件の表示用テキスト */
export function formatDashboardActivity(
  activity: DashboardActivityRow,
  t: TFunction<'translation', undefined>,
  language: string
) {
  const dateTimeDisplay = formatSampleDateTime(new Date(activity.activity_date), language);
  const sampleName = formatEntityDisplayName(
    activity.sample_name || t('common.unknown', { defaultValue: 'Unknown' }),
    t
  );
  const sampleRef = formatActivitySampleRef(activity, t, 'full');
  const sampleRefLine = formatActivitySampleRef(activity, t, 'locationFirst');
  const sampleRefForText = withSampleRefPrefix(sampleRef);

  if (activity.type === 'usage') {
    const detailText = activity.detail ? ` · ${activity.detail}` : '';
    const quantityText = formatUsageQuantitySuffix(activity.quantity, activity.unit, t);
    const rawAction = activity.action?.trim() || '';
    const isFreeNote = Boolean(rawAction && !isKnownUsageAction(rawAction));
    /** フリーノートは別行（狭幅で名前を潰さない）。title 用 text は従来どおり1文 */
    const noteLine = isFreeNote ? `${rawAction}${detailText}${quantityText}` : '';
    /* フリーノートでも動詞を残す（「名前」だけで終わらせない）。メモ本文は noteLine */
    const headlineSuffix = isFreeNote
      ? t('dashboard.activity_note_suffix', { defaultValue: 'を記録しました' })
      : `を${dashboardUsageActionLabel(activity.action, t)}しました${detailText}${quantityText}`;
    const text = isFreeNote
      ? t('dashboard.activity_usage_note_text', {
          defaultValue: '{{sampleName}}{{sampleRef}} · {{note}}{{detail}}{{quantity}}',
          sampleName,
          sampleRef: sampleRefForText,
          note: rawAction,
          detail: detailText,
          quantity: quantityText,
        })
      : t('dashboard.activity_usage_text', {
          defaultValue: '{{sampleName}}{{sampleRef}} · {{action}}しました{{detail}}{{quantity}}',
          sampleName,
          sampleRef: sampleRefForText,
          action: dashboardUsageActionLabel(activity.action, t),
          detail: detailText,
          quantity: quantityText,
        });
    return {
      text,
      sampleName,
      sampleRef,
      sampleRefLine,
      sampleRefTitle: sampleRef,
      headlineSuffix,
      noteLine,
      kindLabel: t('dashboard.activity_kind_usage', { defaultValue: '使用' }),
      dateLabel: t('dashboard.activity_usage_date', { defaultValue: 'Usage date' }),
      /* primary(登録)と隣接しても区別できるよう success（info シアンは近い） */
      badgeColor: 'success' as const,
      dateTimeDisplay,
    };
  }

  const headlineSuffix = t('dashboard.activity_registered_suffix', {
    defaultValue: 'が登録されました',
  });
  return {
    text: t('dashboard.activity_registered_text', {
      defaultValue: '{{sampleName}}{{sampleRef}} · 登録されました',
      sampleName,
      sampleRef: sampleRefForText,
    }),
    sampleName,
    sampleRef,
    sampleRefLine,
    sampleRefTitle: sampleRef,
    headlineSuffix,
    noteLine: '',
    kindLabel: t('dashboard.activity_kind_registered', { defaultValue: '登録' }),
    dateLabel: t('dashboard.activity_registered_date', { defaultValue: 'Registration date' }),
    badgeColor: 'primary' as const,
    dateTimeDisplay,
  };
}
